import React, { useEffect, useState } from 'react';
import { useT } from '../../../../helpers/i18n';
import Skeleton from 'react-loading-skeleton';

import Checkers from '../../../../helpers/class/Checkers';
import { getPropertyBedroomById } from '../../../../api/client';
import CustomModal from '../../../CustomModal';
import SwipperWrapper from '../../properties/partials/SwipperWrapper';

const BED_LABELS = {
  bedroom_beds_king: 'bed.king',
  bedroom_beds_queen: 'bed.queen',
  bedroom_beds_double: 'bed.double',
  bedroom_beds_single: 'bed.single',
  bedroom_beds_sofabed: 'bed.sofabed',
  bedroom_beds_murphy: 'bed.murphy',
  bedroom_bunk: 'bed.bunk',
  bedroom_baby_cribs: 'bed.babyCrib'
};

function bedLines ( bedroom ) {
  if ( bedroom == null ) return [];
  return Object.keys( BED_LABELS )
      .map( ( k ) => ( { label: BED_LABELS[k], n: parseInt( bedroom[k], 10 ) || 0 } ) )
      .filter( ( x ) => x.n > 0 );
}

function featureValue ( feature ) {
  if ( feature.feature_type === 7 && Checkers.isValidArray( feature.feature_options ) ) {
    return feature.feature_options.map( ( o ) => o.option_value ).join( ', ' );
  }
  if ( feature.feature_type === 5 ) {
    return null;
  }
  return feature.feature_value;
}

function BedroomDetail ( { detail } ) {
  const t = useT();
  if ( detail == null ) {
    return (
        <div className="p-t-20">
          <Skeleton height={340} className="col-12 b-rad-lg d-block"/>
          <Skeleton height={27} className="col-12 b-rad-lg d-block"/>
          <Skeleton height={27} className="col-12 b-rad-lg d-block"/>
        </div>
    );
  }

  const beds = bedLines( detail );

  return (
      <div className="p-t-20">
        {
          Checkers.isValidArray( detail.media ) &&
            <div className="m-b-20">
              <SwipperWrapper
                  images={detail.media}
                  imageKey={'image'}
                  slidesPerView={1}
                  galleryHeight={'300px'}
                  paginationBullets={true}
                  wrapperClassName={'Properties__gallery'}
              />
            </div>
        }

        {
          Checkers.isValidString( detail.description ) &&
            <p className="text-justify m-b-20" dangerouslySetInnerHTML={{ __html: detail.description }} />
        }

        {
          Checkers.isValidString( detail.space_level_label ) &&
            <p className="m-b-10">&bull; {t('space.level')} <strong>{detail.space_level_label}</strong></p>
        }

        {
          beds.length > 0 &&
            <>
              <h5 className="m-t-20 m-b-10">{t('bedroom.beds')}</h5>
              {beds.map( ( b ) => <p className="m-b-5" key={b.label}>&bull; {t(b.label)}: <strong>{b.n}</strong></p> )}
            </>
        }

        {
          Checkers.isValidArray( detail.features ) &&
            detail.features.map( ( group ) => (
                Checkers.isValidArray( group.features ) &&
                <div className="m-t-20" key={group.group_id}>
                  <p className="block-title all-caps m-b-10">{group.group_title}</p>
                  <div className="row">
                    {
                      group.features.map( ( feature ) => {
                        const value = featureValue( feature );
                        return (
                            <p className="col-6 m-b-5" key={feature.feature_id}>
                              {feature.feature_type === 5 && <span className="text-success m-r-5">&#10003;</span>}
                              {feature.feature_title}{value != null && value !== '' ? ': ' : ''}
                              {value != null && value !== '' && <strong>{value}</strong>}
                            </p>
                        );
                      } )
                    }
                  </div>
                </div>
            ) )
        }
      </div>
  );
}

export function displayableBedrooms ( bedrooms ) {
  return ( Checkers.isValidArray( bedrooms ) ? bedrooms : [] )
      .filter( ( b ) => /^\d+$/.test( String( b.bedroom_id ) ) )
      .filter( ( b ) => Checkers.isValidArray( b.media ) && b.media.some( ( m ) => Checkers.isValidString( m?.image ) ) );
}

export default function BedroomsModal ( props ) {
  const t = useT();

  const bedrooms = displayableBedrooms( props.bedrooms );

  const [ activeId, setActiveId ] = useState( null );
  const [ detailsById, setDetailsById ] = useState<{ [key: string]: any }>( {} );

  const bedroomIdsKey = bedrooms.map( ( b ) => b.bedroom_id ).join( ',' );

  useEffect( () => {
    if ( activeId == null && bedrooms.length > 0 ) {
      setActiveId( bedrooms[0].bedroom_id );
    }
  }, [ bedroomIdsKey ] );

  useEffect( () => {
    if ( !props.show || props.pageId == null || props.propertyId == null || bedrooms.length === 0 ) {
      return;
    }
    let cancelled = false;
    const pending = bedrooms.filter( ( b ) => detailsById[b.bedroom_id] == null );
    if ( pending.length === 0 ) return;
    const fetchAll = async () => {
      const next: { [key: string]: any } = {};
      await Promise.all( pending.map( async ( b ) => {
        try {
          const res: any = await getPropertyBedroomById( props.pageId, props.propertyId, b.bedroom_id );
          if ( Checkers.isValidArray( res?.bedrooms ) && res.bedrooms[0] != null ) {
            next[b.bedroom_id] = res.bedrooms[0];
          }
        } catch ( e ) {  }
      } ) );
      if ( !cancelled && Object.keys( next ).length > 0 ) {
        setDetailsById( ( prev ) => ( { ...prev, ...next } ) );
      }
    };
    fetchAll();
    return () => { cancelled = true; };
  }, [ props.show, props.pageId, props.propertyId, bedroomIdsKey ] );

  if ( bedrooms.length === 0 ) {
    return null;
  }

  return (
      <CustomModal
          modalName={props.modalName || "bedrooms_modal"}
          className="slide_right"
          modalSize="lg"
          closeModal={props.onClose}
          show={props.show}
          Title={<h3 className="text-overflow mt-0">{props.title || t('property.bedrooms')}</h3>}
      >
        <div className="modal-body">
          <div className="nav-tabs-header nav-tabs-linetriangle">
            <ul className="nav nav-tabs nav-tabs-linetriangle" role="tablist"
                style={{ width: 'auto', whiteSpace: 'nowrap', display: 'inline-block' }}
                data-init-reponsive-tabs="dropdownfx">
              {
                bedrooms.map( ( bedroom, index ) => (
                    <li key={bedroom.bedroom_id}>
                      <a href="javascript:void(0)"
                         className={'fs-12 ' + ( String( activeId ) === String( bedroom.bedroom_id ) ? 'active' : '' )}
                         onClick={() => setActiveId( bedroom.bedroom_id )}>
                        <span>{Checkers.isValidString( bedroom.bedroom_title ) ? bedroom.bedroom_title : t('bedroom.numbered', { n: index + 1 })}</span>
                      </a>
                    </li>
                ) )
              }
            </ul>
          </div>
          {
            activeId != null &&
              <BedroomDetail key={activeId} detail={detailsById[activeId]} />
          }
        </div>
      </CustomModal>
  );
}
